/**
 * SOLID Review
 * S — Strong. One concern: an error the Framework caught, as this page load reports it — which sightings are one
 *     error, and what of it leaves the page.
 * O — Good. A new kind is a value of the schema's enum; a kind that hides its text is one more test in `report`.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Strong. A factory, the ledger's key and the report.
 * D — Strong. The diagnostics schema's types only.
 */
import type { ICommandClientError, IPayloadClientError } from '../ports/schemas/DiagnosticsMoves';

/** Enough of a message to tell two errors apart; the server's fingerprint is the authority. */
const KEY_MESSAGE = 160;

/**
 * A CAUGHT ERROR, AS A VALUE OBJECT (frontend-domain-oo P6, 2026-10-26; handbook §28.273) — the key rule and the report
 * of `decisions/diagnostics/clientError.ts` (client error reporting, plan 2026-10-09, EP5). The page load's caps are
 * the ledger's (`ErrorLedger`).
 */
export class ClientError {
  private constructor(private readonly error: ICommandClientError) {}

  /** The error as the Framework caught it; when it was seen (`now`) is the ledger's business. */
  static of(command: ICommandClientError): ClientError {
    return new ClientError(command);
  }

  /**
   * WHICH SIGHTINGS ARE ONE ERROR, for this page load's ledger: the kind, the port and op of a PORT failure, the
   * message with its numbers folded, and the stack's first frame line. A cheap key, not the server's fingerprint — it
   * only has to keep one loop from sending twice a minute.
   */
  key(): string {
    const { kind, port, op } = this.error;
    const message = this.error.message.replace(/\d+/g, '#').slice(0, KEY_MESSAGE);
    const frame = (this.error.stack ?? '').split('\n').find((line) => /:\d+(:\d+)?\)?\s*$/.test(line))?.replace(/:\d+/g, '').trim() ?? '';
    return `${kind}|${port ?? ''}.${op ?? ''}|${message}|${frame}`;
  }

  /**
   * What leaves the page. A report always says `count: 1` — this page load saw it in this minute — so the server's
   * daily count is "page-load-minutes affected", which no loop can inflate. Only a PORT report names its port, op and
   * code; a THIRD_PARTY one carries no text.
   */
  report(): IPayloadClientError {
    const { kind } = this.error;
    const port = kind === 'PORT';
    const report: IPayloadClientError = {
      kind,
      message: kind === 'THIRD_PARTY' ? '' : this.error.message,
      stack: kind === 'THIRD_PARTY' ? null : this.error.stack,
      port: port ? this.error.port : null,
      op: port ? this.error.op : null,
      code: port ? this.error.code : null,
      count: 1,
    };
    return report;
  }
}
